import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseFileUploadOptions {
  conversationId: string;
  userId: string;
  onUploadComplete: (fileData: {
    url: string;
    name: string;
    type: string;
    size: number;
    duration?: number;
  }) => void;
}

export const useFileUpload = ({ conversationId, userId, onUploadComplete }: UseFileUploadOptions) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const uploadFile = async (file: File, isAudio = false) => {
    if (!file) return;

    const maxSize = isAudio ? 10 * 1024 * 1024 : 50 * 1024 * 1024; // 10MB for audio, 50MB for files
    if (file.size > maxSize) {
      toast({
        title: "File troppo grande",
        description: `Il file deve essere inferiore a ${isAudio ? '10MB' : '50MB'}`,
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${conversationId}/${Date.now()}.${fileExt}`;
      const bucket = isAudio ? 'chat-audio' : 'chat-files';

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Use storage path (works for private buckets with signed URLs later)
      onUploadComplete({
        url: data.path,
        name: file.name,
        type: file.type,
        size: file.size
      });

      toast({
        title: "Upload completato",
        description: "File caricato con successo"
      });

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Errore upload",
        description: "Impossibile caricare il file",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const uploadAudio = async (audioBlob: Blob, duration: number) => {
    const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, {
      type: audioBlob.type
    });

    // Calculate and store duration
    const fileName = `${userId}/${conversationId}/${Date.now()}.webm`;
    
    setUploading(true);

    try {
      const { data, error } = await supabase.storage
        .from('chat-audio')
        .upload(fileName, audioFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Return storage path; we'll create signed URL when displaying
      onUploadComplete({
        url: data.path,
        name: audioFile.name,
        type: audioFile.type,
        size: audioFile.size,
        duration
      });

    } catch (error) {
      console.error('Audio upload error:', error);
      toast({
        title: "Errore upload audio",
        description: "Impossibile caricare l'audio",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    uploadFile,
    uploadAudio
  };
};